"use client";
import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Button from "@/components/form/button";
import {
  getVerifyServiceNumber,
  getVerifyMobileNumber,
  getComplaintStatus,
  getFetchBillDetails,
  getFetchBillHistory,
  getFetchPaymentHistory,
  getSupplyInterruptionStatus,
  getStaffPhoneNumbers,
  getDownloadVerifyServiceNumber,
  getLTBillHtml,
} from "@/actions/actions";
import Link from "next/link";
import Rocket from "@/components/icons/rocket";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [formAction, setFormAction] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [menu, setMenu] = useState([]);
  const messagesEndRef = useRef(null);

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const complaintTypesUrl = "/assets/js/complaint_type.json?q=03062025";

  const handleMenuClick = (item) => {
    addMessage("user", item.value);
    item.action();
  };

  const mainMenu = () => {
    return [
      { id: "2", value: "New Service Registration", action: newService },
      { id: "3", value: "Bill Info", action: billinfo },
      { id: "4", value: "Know your supply status", action: supplydetails },
      { id: "5", value: "Other Services", action: otherservices },
      { id: "6", value: "KRESCO Online Services", action: onlineservices },
      { id: "1", value: "Grievance", action: grievance },
    ];
  };

  const resetChat = () => {
    setMessages([]);
    setInputValue("");
    setFormAction(null);
    setMenu(mainMenu());
  };

  useEffect(() => {
    resetChat();
  }, []);

  const addMessage = (sender, text) => {
    setMessages((prev) => [...prev, { sender, text }]);
  };

  // Grievance
  const grievance = () => {
    addMessage("bot", "Please select from following to help you");
    setMenu([
      { id: "11", value: "Register Complaint", action: registerComplaint },
      { id: "12", value: "Complaint Status", action: complaintStatus },
    ]);
  };

  const registerComplaint = async () => {
    const res = await fetch(complaintTypesUrl);
    const json = await res.json();
    const complaintTypes = Object.entries(json.complaint_types).map(
      ([key, value]) => ({
        id: key,
        value,
        action: () => loadSubComplaintType(key, value),
      })
    );
    addMessage("bot", "Please select from following Complaint types");
    setMenu(complaintTypes);
  };

  const loadSubComplaintType = async (typeId, typeValue) => {
    const res = await fetch(complaintTypesUrl);
    const json = await res.json();
    const subtypes = json.subcomplaint_types[typeId]?.subtype || {};
    const menuItems = Object.entries(subtypes).map(([key, value]) => ({
      id: key,
      value,
      action: () => requestServiceNumber(typeId, typeValue, key, value),
    }));
    addMessage("bot", "Please select from following Sub-Complaint types");
    setMenu(menuItems);
  };

  const requestServiceNumber = (typeId, typeValue, subTypeId, subTypeValue) => {
    addMessage("bot", "Please enter your 13-digit/10-digit service number");
    setMenu([]);
    setFormAction(
      () => (val) =>
        verifyServiceNumber(val, typeId, typeValue, subTypeId, subTypeValue)
    );
  };

  const verifyServiceNumber = async (
    servicenumber,
    typeId,
    typeValue,
    subTypeId,
    subTypeValue
  ) => {
    const regex = /^\d{13}$/;
    if (!regex.test(servicenumber)) {
      addMessage("bot", "please Enter valid Service number");
      setFormAction(
        () => (val) =>
          verifyServiceNumber(val, typeId, typeValue, subTypeId, subTypeValue)
      );
      return;
    }

    try {
      const data = await getVerifyServiceNumber(servicenumber);

      if (data.status === "success") {
        addMessage(
          "bot",
          "Please enter your mobile number for sending SMS and feedback call"
        );
        setMenu([]);
        setFormAction(
          () => (mobile) =>
            submitComplaint(servicenumber, mobile, typeId, subTypeId)
        );
      } else if (data.message === "Connection info not available") {
        addMessage("bot", "Invalid Service Number");
        setFormAction(
          () => (val) =>
            verifyServiceNumber(val, typeId, typeValue, subTypeId, subTypeValue)
        );
      } else {
        addMessage("bot", data.message || "Something went wrong");
      }
    } catch (error) {
      addMessage("bot", "Unable to fetch details");
      setFormAction(
        () => (val) =>
          verifyServiceNumber(val, typeId, typeValue, subTypeId, subTypeValue)
      );
    }
  };

  const submitComplaint = async (
    servicenumber,
    mobile,
    category,
    subcategory
  ) => {
    const regex = /^\d{10}$/;

    if (!regex.test(mobile)) {
      addMessage("bot", "Please enter a valid 10-digit mobile number");
      // Prompt again for mobile number
      setFormAction(
        () => (val) =>
          submitComplaint(servicenumber, val, category, subcategory)
      );
      return;
    }

    try {
      const data = await getVerifyMobileNumber(
        servicenumber,
        mobile,
        category,
        subcategory
      );

      if (data.status === "success") {
        addMessage("bot", `Complaint registered. ID: ${data.complaintid}`);
        setFormAction(null);
      } else {
        addMessage("bot", "Unable to register complaint. Please try again.");
        // Prompt for mobile again in case it's a mobile-related failure
        setFormAction(
          () => (val) =>
            submitComplaint(servicenumber, val, category, subcategory)
        );
      }
    } catch (error) {
      addMessage(
        "bot",
        "Something went wrong while submitting your complaint."
      );
      setFormAction(
        () => (val) =>
          submitComplaint(servicenumber, val, category, subcategory)
      );
    }
  };

  const complaintStatus = () => {
    addMessage("bot", "Please enter your 10-digit complaint ID");
    setMenu([]);
    setFormAction(() => handleFetchComplaintStatus);
  };

  const handleFetchComplaintStatus = async (complaintid) => {
    const regex = /^\d{10}$/;
    if (!regex.test(complaintid)) {
      addMessage("bot", "Invalid Complaint ID");
      return;
    }

    const data = await getComplaintStatus(complaintid);

    if (!data) {
      addMessage(
        "bot",
        "Failed to retrieve complaint status. Please try again."
      );
      return;
    }

    let status = "Unknown";
    switch (data.ticket_status) {
      case "CREATED":
        status = "Complaint Registered";
        break;
      case "CLOSED":
        status = "Issue Resolved";
        break;
      case "COMPLETED":
        status = "Positive Feedback";
        break;
      case "REOPEN":
        status = "Complaint Reopened";
        break;
    }

    addMessage("bot", `Status: ${status}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    addMessage("user", inputValue);
    formAction && formAction(inputValue.trim());
    setInputValue("");
  };

  // 2 New Service
  const newService = () => {
    addMessage(
      "bot",
      <span>
        Please{" "}
        <Link
          className="text-green-2 underline"
          href="/new-connection/lt-new-connection-registration"
          target="_blank"
          rel="noopener noreferrer"
        >
          click
        </Link>{" "}
        here to register for LT service.
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };

  // 3 Bill Info
  const billinfo = () => {
    addMessage("bot", "Please select from following to help you");
    setMenu([
      { id: "1", value: "Know your Bill details", action: knowyourbilldetails },
      { id: "2", value: "Pay your bill", action: paybillonline },
      { id: "3", value: "Bill history", action: billHistory },
      { id: "4", value: "Payment history", action: paymentHistory },
    ]);
  };
  // Know your bill
  const knowyourbilldetails = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => fetchBillDetails(val));
  };
  const fetchBillDetails = async (servicenumber) => {
    const re = /^\d{13}$/;
    const htre = /^[a-zA-Z]{3}\d{1,5}$/;

    if (servicenumber && (re.test(servicenumber) || htre.test(servicenumber))) {
      setFormAction(null);

      try {
        const data = await getFetchBillDetails(servicenumber);
        if (data.status === "success") {
          const htmlMessage = (
            <span>
              Dear <strong>{data.consName}</strong>, your billed amount for
              service number <strong>{servicenumber}</strong> is{" "}
              <strong>Rs. {data.totaldueamt}</strong> and due date is{" "}
              <strong>{data.dueDt}</strong>.{" "}
              <Link
                href="/digital_payment.php"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-2 underline"
              >
                Click here
              </Link>{" "}
              to pay your power bill online.
            </span>
          );
          addMessage("bot", htmlMessage);
        } else {
          addMessage("bot", data.message || "Could not retrieve bill details.");
        }
      } catch (error) {
        console.error("Bill details fetch error:", error);
        addMessage("bot", "Unable to fetch details. Please try again later.");
      }

      exitCheck();
    } else {
      addMessage("bot", "Please enter a valid 10 or 13-digit service number.");
      setFormAction((input) => fetchBillDetails(input));
    }
  };
  // Pay your bill
  const paybillonline = () => {
    addMessage(
      "bot",
      <span>
        <Link
          className="text-green-2 underline"
          href="/digital_payment.php"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </Link>{" "}
        to pay your power bill online.
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };
  // Bill history
  const billHistory = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => fetchBillHistory(val));
  };
  const fetchBillHistory = async (servicenumber) => {
    const re = /^\d{13}$/;
    const htre = /^[a-zA-Z]{3}\d{1,5}$/;

    if (servicenumber && (re.test(servicenumber) || htre.test(servicenumber))) {
      setFormAction(null);
      try {
        const data = await getFetchBillHistory(servicenumber);

        if (data.status === "success" && Array.isArray(data.data)) {
          const billList = data.data.slice(0, 7).map((entry, i) => {
            const date =
              entry.closingdate || entry.closingDate || "Unknown Date";
            const amount = entry.billamount || entry.billAmount || "0";
            return (
              <div key={i} className="border-b py-1 text-sm">
                {date} - Rs. {amount}
              </div>
            );
          });

          addMessage(
            "bot",
            <div>
              <strong>Bill History for {servicenumber}</strong>
              <div className="mt-2 bg-gray-50 border rounded p-2">
                {billList}
              </div>
            </div>
          );
        } else {
          addMessage("bot", data.message);
          billHistory();
          return;
        }
      } catch (error) {
        console.error("Bill history fetch error:", error);
        addMessage("bot", "Unable to fetch details");
      }

      exitCheck();
    } else {
      addMessage("bot", "please Enter valid Service number");
      setFormAction((input) => fetchBillHistory(input));
    }
  };
  // Payment history
  const paymentHistory = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => fetchPaymentHistory(val));
  };
  const fetchPaymentHistory = async (servicenumber) => {
    const re = /^\d{13}$/;
    const htre = /^[a-zA-Z]{3}\d{1,5}$/;

    if (servicenumber && (re.test(servicenumber) || htre.test(servicenumber))) {
      setFormAction(null);

      try {
        const data = await getFetchPaymentHistory(servicenumber);

        if (data.status === "success" && Array.isArray(data.data)) {
          const historyList = data.data.slice(0, 7).map((entry, i) => (
            <div key={i} className="border-b py-1 text-sm">
              {entry.prdate} - Rs. {entry.billamt}
            </div>
          ));

          addMessage(
            "bot",
            <div>
              <strong>Payment History for {servicenumber}</strong>
              <div className="mt-2 bg-gray-50 border rounded p-2">
                {historyList}
              </div>
            </div>
          );
          exitCheck();
        } else {
          addMessage("bot", data.message);
          paymentHistory();
        }
      } catch (error) {
        console.error("Payment history fetch error:", error);
        addMessage("bot", "Unable to fetch details. Please try again later.");
        exitCheck();
      }
    } else {
      addMessage("bot", "Please enter a valid 10 or 13-digit service number.");
      setFormAction((input) => fetchPaymentHistory(input));
    }
  };

  // 4 Know your supply status
  const supplydetails = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => fetchsuplydetails(val));
  };
  const fetchsuplydetails = async (servicenumber) => {
    const regex = /^\d{13}$/;
    if (!regex.test(servicenumber)) {
      addMessage("bot", "Invalid Service number");
      setFormAction(() => (val) => fetchsuplydetails(val));
      return;
    }
    setFormAction(null);
    const data = await getSupplyInterruptionStatus(servicenumber);
    if (data.status === "success") {
      const interruptData = data.interruption;

      if (interruptData && "STATUS" in interruptData) {
        if (interruptData.STATUS === "TRUE") {
          const response = interruptData.RESPONSE;
          const msg = `Interruption due to ${response.REASON} from ${response.OFF_DATE} ${response.OFF_TIME} till ${response.RESTORE_DATE} ${response.RESTORE_TIME} for the feeder ${response.FEEDER_NAME}`;
          addMessage("bot", `Supply status for ${servicenumber}`);
          addMessage("bot", msg);
        } else {
          addMessage("bot", "Currenty There are no interruptions In your area");
        }
      } else {
        addMessage("bot", "Unable to fetch details");
      }
      exitCheck();
    } else {
      addMessage("bot", data.message);
      supplydetails();
    }
  };

  // 5 Other Services
  const otherservices = () => {
    addMessage("bot", "Please select from following to help you");
    setMenu([
      { id: "1", value: "Other Registrations", action: otherregistrations },
      { id: "2", value: "Calculate energy", action: calculateenergy },
      {
        id: "3",
        value: "Know your category of Supply",
        action: knowyourcategorysupply,
      },
    ]);
  };
  // Other registrations
  const otherregistrations = () => {
    addMessage(
      "bot",
      <span>
        <Link
          className="text-green-2 underline"
          href="/new-connection/service-request-registration"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </Link>{" "}
        for “Other Registrations” from KRESCO
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };
  // Calculate Energy
  const calculateenergy = () => {
    addMessage(
      "bot",
      <span>
        <Link
          className="text-green-2 underline"
          href="/energy-calculator"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </Link>{" "}
        to calculate your consumption based on your load.
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };
  // know your category supply
  const categoryofsupplydetails = [
    {
      key: "domestic premises, state/central government residential complexes",
      value: "Category I (A)",
    },
    {
      key: "townships, colonies, gated communities, villas",
      value: "Category I (B)",
    },
    {
      key: "non-domestic premises like shops, business houses, offices, public buildings, hospitals, hotels, choultries, restaurants, clubs, theatres, cinema halls, bus stations, railway stations, timber depots, photo studios, printing presses, advertising hoardings, function halls/auditoriums, educational institutions run by individuals or NGOs or private trusts and their student hostels, airports, resorts, amusement parks, MICE centers, golf courses, botanical gardens, urban/rural haats, tourism and hospitality training institutes, wayside amenities, spiritual/wellness centres and museums",
      value: "Category II (A)",
    },
    {
      key: "startup power for captive generating plants, co-generating plants and renewable energy generation plants, merchant plants",
      value: "Category II (B)",
    },
    { key: "electric vehicles/charging stations", value: "Category II (C)" },
    { key: "green power", value: "Category II (D)" },
    {
      key: "industries for purpose of manufacturing, processing/preserving goods for sale, water works & sewerage pumping stations operated by government departments or co-operative societies and pumpsets of railways, pumping of water by industries as subsidiary function and sewerage pumping station operated by local bodies. Workshops, flour mills, oil mills, saw mills, office grinders and wet grinders, ice candy units with or without sale outlets, grass cutting and fodder cutting units, IT units approved by CCITIT, newspaper printing units, aqua hatcheries and aqua feed mixing plants, poultry hatcheries and feed mixing plants",
      value: "Category III (A)",
    },
    {
      key: "seasonal industries (off season) like manufacturing of sugar or ice or salt, decorticating, seed processing, fruit processing, ginning and pressing, cotton seed oil mills, tobacco processing, re-drying and rice mills or any other industries or processes approved by commission from time to time",
      value: "Category III (B)",
    },
    {
      key: "energy intensive industries",
      value:
        "Category III (C) (This category is available only to HT consumers)",
    },
    {
      key: "cottage industries upto 10HP like dhobighats & bonafide small cottage industries specifically power looms, carpentry, blacksmithy, kanchari, goldsmithy, shilpi, pottery, mochy, phenol production units, agarbatti production units, wax candle making units, papads manufacturing units, leather (chappals) making, soap industry, plaster of paris units, laque toy making units, POP toys, wood carving/toy making units, pickles manufacturing, mango jelly units, adda leaf plate industry",
      value: "Category III (D)",
    },
    {
      key: "lighting on public roads, streets, thoroughfare including parks, markets, car-stands, taxi stands, bridges, PWS schemes in local bodies, NTR Sujala Pathkam schemes notified by Government of AP or any other statutory authority",
      value: "Category IV (A)",
    },
    {
      key: "crematoria, government educational institutions, government offices, government hospitals, student hostels run by govt agencies, public charitable institutions, recognized service institutions, registered old age homes, orphanages",
      value: "Category IV (B)",
    },
    {
      key: "temples, churches, mosques, gurudwaras, goshalas",
      value: "Category IV (C)",
    },
    {
      key: "railway traction",
      value:
        "Category IV (D) (This category is available only to HT consumers)",
    },
    {
      key: "agriculture, corporate farmers, non-corporate farmers, salt farming units upto 15 HP, sugarcane crushing, rural horticulture nurseries, floriculture in greenhouse",
      value: "Category V (A)",
    },
    { key: "aquaculture and animal husbandry", value: "Category V (B)" },
    {
      key: "agro-based cottage industries upto 10 HP like sisal fiber extraction cooperative units, vermi culture, seri culture, mushroom growing/farming, rabbit farming, sheep rearing, emu birds farming, apiculture (honey making), chaff-cutting and dairy farming activities",
      value: "Category V (D)",
    },
    {
      key: "government/private lift irrigation schemes",
      value: "Category V (E)",
    },
  ];
  const knowyourcategorysupply = () => {
    addMessage("bot", "Please enter purpose of supply");
    // addMessage("bot", "Please enter purpose of supply (e.g., shop, office, hospital, etc.)");
    setMenu([]);
    setFormAction(() => fetchcategoryofsupply);
  };
  const fetchcategoryofsupply = (input) => {
    const query = input.trim().toLowerCase();

    if (query.length <= 3) {
      addMessage(
        "bot",
        "Please enter more than 3 letters for purpose of supply."
      );
      knowyourcategorysupply();
      return;
    }
    const matches = categoryofsupplydetails.filter((item) =>
      item.key.toLowerCase().includes(query)
    );
    if (matches.length > 0) {
      const resultMessage = (
        <div>
          <p className="font-semibold">Your Category of supply is:</p>
          <div className="mt-2 border border-gray-200 rounded">
            {matches.map((item, i) => (
              <div key={i} className="p-2 border-b last:border-0">
                <p className="text-gray-700 text-sm">{item.key}</p>
                <p className="text-green-800 font-bold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      );
      addMessage("bot", resultMessage);
      exitCheck();
    } else {
      addMessage(
        "bot",
        "Unable to find category. Please enter a valid purpose of supply."
      );
      knowyourcategorysupply();
    }
  };

  // 6 Online Services
  const onlineservices = () => {
    addMessage("bot", "Please select from following to help you");
    setMenu([
      { id: "1", value: "RESCO Consumer App", action: krescoconsumerapp },
      { id: "2", value: "RESCO staff phone numbers", action: staffphonenumber },
      { id: "3", value: "Feedback and Suggestions", action: feedback },
      { id: "4", value: "Download Your latest bill", action: billDownload },
      { id: "5", value: "Download Tariff Order", action: tarifforder },
    ]);
  };
  // Consumer App
  const krescoconsumerapp = () => {
    addMessage(
      "bot",
      <span>
        <Link
          className="text-green-2 underline"
          href="/#"
          target="_blank"
          rel="noopener noreferrer"
        >
          Click here
        </Link>{" "}
        to download KRESCO Consumer App
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };
  // Staff Number
  const staffphonenumber = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => fetchstaffphonenumber(val));
  };
  const fetchstaffphonenumber = async (servicenumber) => {
    const regex = /^\d{13}$/;

    if (!regex.test(servicenumber)) {
      addMessage("bot", "Invalid Service number");
      setFormAction(() => (val) => fetchstaffphonenumber(val));
      return;
    }
    const response = await getStaffPhoneNumbers(servicenumber);
    if (response.status === "success") {
      const { lmmobile, aemobile, ademobile, aaomobile } = response.data;
      const message = (
        <div className="mt-2 border rounded border-gray-300">
          <div className="p-2 text-green-2 font-semibold">
            Here is the list of phone numbers of
          </div>
          <div className="p-2 font-semibold">KRESCO Staff {servicenumber}</div>
          <div className="p-2">Lineman (LM): {lmmobile}</div>
          <div className="p-2">AE: {aemobile}</div>
          <div className="p-2">ADE: {ademobile}</div>
          <div className="p-2">AAO: {aaomobile}</div>
          <div className="p-2 text-green-2 font-semibold">
            Or contact 1912 Toll-free number for any complaints.
          </div>
        </div>
      );
      addMessage("bot", message);
      exitCheck();
    } else {
      addMessage("bot", response.message);
      setFormAction(() => (val) => fetchstaffphonenumber(val));
    }
  };
  // feedback
  const feedback = () => {
    addMessage("bot", "Please type your feedback/suggestions and send");
    setMenu([]);
    setFormAction(() => (val) => feedbackform(val));
  };
  const feedbackform = (input) => {
    const sanitized = input.replace(/[^\w\s]/gi, "").trim();
    if (!sanitized) {
      feedback();
      return;
    }
    addMessage("bot", "Thank you for your feedback/suggestions!");
    exitCheck();
  };
  // Download Your Latest Bill
  const billDownload = () => {
    addMessage("bot", "Please enter your Service number");
    setMenu([]);
    setFormAction(() => (val) => billDownloadForm(val));
  };
  const billDownloadForm = async (servicenumber) => {
    const isLT = /^\d{13}$/.test(servicenumber);
    if (isLT) {
      const data = await getDownloadVerifyServiceNumber(servicenumber);
      if (data?.status === "success") {
        addMessage(
          "bot",
          <span>
            Please{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                showLTBill(servicenumber);
              }}
              className="text-green-2 underline"
            >
              click here
            </a>{" "}
            to download your bill.
          </span>
        );
      } else {
        addMessage("bot", "Invalid service number");
        billDownload();
      }
    } else {
      addMessage("bot", "Invalid service number");
      billDownload();
    }

    exitCheck();
  };
  const showLTBill = async (servicenumber) => {
    const data = await getLTBillHtml(servicenumber);
    if (!data) {
      addMessage("bot", "Unable to load your bill. Please try again later.");
      return;
    }
    const win = window.open("", "KRESCO BILL", "width=1080,height=800");
    win.document.write(data);
    win.document.close();
  };
  // Download Tariff Bill
  const tarifforder = () => {
    addMessage(
      "bot",
      <span>
        <Link
          className="text-green-2 underline"
          href="/customer-info/electricity-tariff"
          target="_blank"
          rel="noopener noreferrer"
        >
          click here
        </Link>{" "}
        to download Tariff order.
      </span>
    );
    setMenu([]);
    setFormAction(null);
    exitCheck();
  };

  // Attach globally for use in <a onclick="...">
  // if (typeof window !== "undefined") {
  //   window.showLTBill = showLTBill;
  // }
  useEffect(() => {
  window.showLTBill = showLTBill;
}, []);

  // Exit
  const exitCheck = () => {
    addMessage("bot", "Do you like to continue?");
    setMenu([
      { id: "yes", value: "Yes", action: resetChat },
      { id: "no", value: "No", action: exitconfirm },
    ]);
  };

  const exitfinal = () => {
    addMessage("bot", "Thank you. Have a nice day");
    setMenu([]);
  };

  const exitconfirm = () => {
    addMessage("bot", "Are you sure you want to exit ?");
    setMenu([
      { id: "yes", value: "Yes", action: exitfinal },
      { id: "no", value: "No", action: resetChat },
    ]);
  };

  return (
    <div className="fixed bottom-24 right-8 z-50">
      <div
        onClick={() => {
          toggleChat(), resetChat();
        }}
        className="cursor-pointer"
      >
        <Image
          width={64}
          height={64}
          src="/assets/images/new_design/ammu.webp"
          alt="Ammu"
          className="w-14 h-14 bg-white animate-[pulse_1.3s_ease-in-out_infinite] delay-600 rounded-full p-2 transition-all hover:animate-none hover:rotate-180 hover:scale-110 hover:duration-300"
          sizes="(min-width: 1280px) 56px, 70px"
        />
      </div>

      {isOpen && (
        <div className="fixed bottom-6 md:bottom-4 right-4 md:right-28 w-[90%] md:w-[410px] bg-white shadow-xl rounded-lg">
          <div className="bg-green-9 text-white px-3 flex justify-between items-center rounded-t-lg">
            <h3 className="font-semibold">Ask Resco Assist</h3>
            <div className="flex justify-center items-center">
              <Button
                onClick={resetChat}
                className="text-white text-xl bg-transparent"
                title="Restart Chat"
                text="⟳"
              />
              <Button
                onClick={() => {
                  resetChat();
                  toggleChat();
                }}
                className="text-white text-2xl bg-transparent"
                title="Close"
                text="×"
              />
            </div>
          </div>

          <div className="h-fit max-h-[460px] md:max-h-[400px] xl:max-h-[500px] px-2 py-3 overflow-y-auto">
            <p className="bg-green-10 text-sm p-3 mt-1 mb-4 rounded-lg w-fit">
              Hi 👋 Resco Assist here. Please choose your option
            </p>

            <div className="flex flex-col w-full space-y-2">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`max-w-xs px-4 py-2 rounded-lg text-sm ${
                    msg.sender === "bot"
                      ? "bg-green-3 text-left self-start"
                      : "bg-green-4 text-right self-end ml-auto"
                  }`}
                >
                  {msg.text}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {menu.length > 0 && (
              <div className="w-[65%] mt-4">
                {menu.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item)}
                    className="my-1 py-2 px-2 w-full text-left bg-white text-green-12 border border-green-200 rounded-md hover:bg-gray-100 transition"
                  >
                    {item.value}
                  </button>
                ))}
              </div>
            )}

            {formAction && (
              <form onSubmit={handleSubmit} className="w-full mt-4">
                <div className="flex space-x-4">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Type your message..."
                    className="flex-1 rounded-lg p-2 focus:outline-none focus:ring-1 focus:ring-green-2"
                  />
                  <Button
                    type="submit"
                    aria-label="rocket button"
                    className="bg-green-2 text-white px-4 rounded-r-lg"
                  >
                    <Rocket />
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chatbot;

